import React from "react";
import classNames from "classnames";
import { useTranslation } from "react-i18next";
import {
  useEditProjectDetailsConfiguration,
  useRetrieveCurrentPlanzone,
  useRetrieveProjectDetailsConfiguration,
  useRolesRetrieval,
} from "@planzone/view-models";
import { Id, ProjectDetailsConfiguration as Config } from "@planzone/dto";
import { useMessage } from "@components/useMessage";
import { events } from "@planzone/events";
import { Button } from "@common/button";
import { Modal } from "@common/modal";
import { HorizontalList } from "@common/horizontalList";
import { Form } from "./form";
import { Props } from "./types";
import styles from "./styles.scss";

function canSubmitForm(profile: Config[number]) {
  return profile.roles.length > 0 && profile.elements.length > 0;
}

function ProjectDetailsConfiguration({ project, cancel }: Props) {
  const { t } = useTranslation();
  const { roles } = useRolesRetrieval();
  const { planzone } = useRetrieveCurrentPlanzone();
  const { configuration } = useRetrieveProjectDetailsConfiguration(planzone);
  const {
    isRequestPending: isEditionRequestPending,
    editProjectDetailsConfiguration,
  } = useEditProjectDetailsConfiguration(planzone);
  const [formData, setFormData] = React.useState(configuration);
  const [profileToAdd, setProfileToAdd] = React.useState<Config[number] | null>(
    null,
  );
  const [currentProfile, setCurrentProfile] = React.useState(0);
  const usedRoles = formData
    .reduce<Id[]>((roles, profile) => [...roles, ...profile.roles], [])
    .filter((role) =>
      !profileToAdd && formData[currentProfile]
        ? !formData[currentProfile].roles.find((r) => r === role)
        : true,
    );
  const remainingRoles = roles
    .filter((role) => role.name === "pz_admin" || role.name === "pr_manager")
    .filter(
      (role) =>
        ![
          ...(formData[currentProfile] ? formData[currentProfile].roles : []),
          ...usedRoles,
        ].find((id) => id === role.name || role.uuid === id),
    );
  const canSubmit = canSubmitForm(
    !!profileToAdd
      ? profileToAdd
      : !!formData[currentProfile]
        ? formData[currentProfile]
        : {
            name: "",
            roles: [],
            elements: [],
          },
  );

  useMessage(({ type, data }) => {
    if (type === events.editProjectDetailsConfigurationSucceeded) {
      setProfileToAdd(null);
      data.configuration.length !== configuration.length &&
        setCurrentProfile(data.configuration.length - 1);
    }
  });

  React.useEffect(() => {
    setFormData(configuration);
  }, [configuration]);

  return (
    <Modal header={t("projectDetailsConfiguration.title")} close={cancel}>
      <HorizontalList>
        <div className={styles.tabs}>
          {remainingRoles.length > 0 && (
            <p
              className={classNames(styles.tab, {
                [styles.selectedTab]: !!profileToAdd,
              })}
              onClick={() =>
                setProfileToAdd({
                  name: "",
                  roles: remainingRoles.map(({ name }) => name),
                  elements: [
                    {
                      type: "column",
                      label: {
                        fr: "",
                        en: "",
                      },
                      key: "strategicScore",
                      elements: [],
                    },
                    {
                      type: "column",
                      label: {
                        fr: "",
                        en: "",
                      },
                      key: "projectManagers",
                      elements: [],
                    },
                    {
                      type: "section",
                      label: {
                        fr: "Suivi de l'activité",
                        en: "Activity monitoring",
                      },
                      key: "activityMonitoring",
                      elements: [
                        {
                          type: "column",
                          label: {
                            fr: "",
                            en: "",
                          },
                          key: "activityMonitoring_done",
                          elements: [],
                        },
                        {
                          type: "column",
                          label: {
                            fr: "",
                            en: "",
                          },
                          key: "activityMonitoring_delayed",
                          elements: [],
                        },
                        {
                          type: "column",
                          label: {
                            fr: "",
                            en: "",
                          },
                          key: "activityMonitoring_opened",
                          elements: [],
                        },
                      ],
                    },
                    {
                      type: "section",
                      label: {
                        fr: "Travail",
                        en: "Work",
                      },
                      key: "workload",
                      elements: [
                        {
                          type: "column",
                          label: {
                            fr: "",
                            en: "",
                          },
                          key: "workload_allocatedTime",
                          elements: [],
                        },
                        {
                          type: "column",
                          label: {
                            fr: "",
                            en: "",
                          },
                          key: "workload_spentTime",
                          elements: [],
                        },
                        {
                          type: "column",
                          label: {
                            fr: "",
                            en: "",
                          },
                          key: "workload_remainingWork",
                          elements: [],
                        },
                      ],
                    },
                    {
                      type: "section",
                      label: {
                        fr: "Budget",
                        en: "Budget",
                      },
                      key: "budget",
                      elements: [
                        {
                          type: "column",
                          label: {
                            fr: "",
                            en: "",
                          },
                          key: "budget_allocatedBudget",
                          elements: [],
                        },
                        {
                          type: "column",
                          label: {
                            fr: "",
                            en: "",
                          },
                          key: "budget_plannedBudget",
                          elements: [],
                        },
                        {
                          type: "column",
                          label: {
                            fr: "",
                            en: "",
                          },
                          key: "budget_realCosts",
                          elements: [],
                        },
                        {
                          type: "column",
                          label: {
                            fr: "",
                            en: "",
                          },
                          key: "budget_plannedMargin",
                          elements: [],
                        },
                        {
                          type: "column",
                          label: {
                            fr: "",
                            en: "",
                          },
                          key: "budget_realMargin",
                          elements: [],
                        },
                        {
                          type: "column",
                          label: {
                            fr: "",
                            en: "",
                          },
                          key: "budget_acquiredValue",
                          elements: [],
                        },
                      ],
                    },
                  ],
                })
              }
            >
              + {t("projectDetailsConfiguration.addProfile")}
            </p>
          )}
          {formData.map(({ name }, index) => (
            <p
              key={index}
              className={classNames(styles.tab, {
                [styles.selectedTab]: index === currentProfile && !profileToAdd,
              })}
              onClick={() => {
                setProfileToAdd(null);
                setCurrentProfile(index);
              }}
            >
              {name}
            </p>
          ))}
        </div>
      </HorizontalList>
      <form
        className={styles.projectDetailsConfiguration}
        onSubmit={(e) => {
          e.preventDefault();

          !isEditionRequestPending &&
            editProjectDetailsConfiguration({
              planzone,
              configuration,
              nextConfiguration: !profileToAdd
                ? formData
                : [...formData, profileToAdd],
            });
        }}
      >
        {!!formData[currentProfile] && !profileToAdd && (
          <div className={styles.form}>
            <Form
              project={project}
              formData={formData[currentProfile]}
              usedRoles={usedRoles}
              edit={(profile) =>
                setFormData([
                  ...formData.slice(0, currentProfile),
                  profile,
                  ...formData.slice(currentProfile + 1),
                ])
              }
            />
          </div>
        )}
        {!!profileToAdd && (
          <div className={styles.form}>
            <Form
              project={project}
              formData={profileToAdd}
              usedRoles={usedRoles}
              edit={(profile) => setProfileToAdd(profile)}
            />
          </div>
        )}
        <div className={styles.footer}>
          {!profileToAdd && (
            <div>
              <Button
                type="delete"
                status={isEditionRequestPending ? "pending" : "none"}
                isDisabled={isEditionRequestPending}
                press={() => {
                  editProjectDetailsConfiguration({
                    planzone,
                    configuration,
                    nextConfiguration: [
                      ...configuration.slice(0, currentProfile),
                      ...configuration.slice(currentProfile + 1),
                    ],
                  });
                  setCurrentProfile(0);
                }}
              >
                {t("projectDetailsConfiguration.remove")}
              </Button>
            </div>
          )}
          <div>
            <Button type="cancel" press={cancel}>
              {t("projectDetailsConfiguration.cancel")}
            </Button>
            <Button
              type="submit"
              status={isEditionRequestPending ? "pending" : "none"}
              isDisabled={isEditionRequestPending || !canSubmit}
            >
              {!profileToAdd
                ? t("projectDetailsConfiguration.edit")
                : t("projectDetailsConfiguration.add")}
            </Button>
          </div>
        </div>
      </form>
    </Modal>
  );
}

export { ProjectDetailsConfiguration };
